/**
 * In-app support & feedback.
 *
 * These types are the wire contract between the web widget and
 * `POST /feedback` on the user service. Constants are shared so the client
 * and the server enforce the same bounds — the client copy is UX, the server
 * copy is the one that matters.
 */

/** What the reporter says the report is. Drives the Slack channel it lands in. */
export enum FeedbackType {
  BUG = 'bug',
  HELP = 'help',
  SUGGESTION = 'suggestion',
}

/** Enumerable form of {@link FeedbackType} for schema/validation use. */
export const FEEDBACK_TYPES = Object.values(FeedbackType)

export const FEEDBACK_MESSAGE_MIN_LENGTH = 10
export const FEEDBACK_MESSAGE_MAX_LENGTH = 2000

/**
 * Hard cap on the request body. Everything inside the payload is separately
 * bounded, so a body over this size is either a bug or someone probing.
 */
export const FEEDBACK_MAX_PAYLOAD_BYTES = 32 * 1024

/** Depth of the client-side circular error buffer. */
export const FEEDBACK_LAST_ERRORS_MAX = 5
export const FEEDBACK_ERROR_MESSAGE_MAX_LENGTH = 500
export const FEEDBACK_ERROR_STACK_MAX_LENGTH = 2000

/** Bounds on the free-text context fields, so a forged payload can't be huge. */
export const FEEDBACK_URL_MAX_LENGTH = 2048
export const FEEDBACK_USER_AGENT_MAX_LENGTH = 512
export const FEEDBACK_SHORT_FIELD_MAX_LENGTH = 128

/**
 * Rate limits. The client enforces the 30s gap as UX (disabled button with a
 * countdown); the server enforces both as the actual control.
 */
export const FEEDBACK_MIN_SUBMIT_INTERVAL_SECONDS = 30
export const FEEDBACK_HOURLY_SUBMIT_LIMIT = 20

/**
 * One entry from the client's circular buffer of unhandled errors. Captured
 * at app bootstrap so a report filed after something broke carries the stack.
 */
export type FeedbackClientError = {
  message: string
  stack?: string
  /** ISO 8601. */
  timestamp: string
}

/**
 * Technical context gathered by the widget. Everything here is
 * client-supplied and therefore untrusted — identity is resolved server-side
 * and never read from this object.
 */
export type FeedbackContext = {
  /** Full location the reporter was on, including query string. */
  url: string
  /** Matched route pattern (e.g. `/projects/$projectId`), not the raw path. */
  route: string
  referrer?: string
  /** Git SHA baked into the bundle at build time. `unknown` when not injected. */
  buildSha: string
  userAgent: string
  /** `<width>x<height>` CSS pixels. */
  viewport: string
  /** IANA zone, e.g. `Australia/Brisbane`. */
  timezone: string
  /** ISO 8601, from the reporter's clock. */
  clientTimestamp: string
  lastErrors: FeedbackClientError[]
}

/** The request body of `POST /feedback`. */
export type SubmitFeedbackInput = {
  type: FeedbackType
  message: string
  /** Prefilled from the session but editable, so it may differ from the account email. */
  reporterEmail: string
  context: FeedbackContext
}

/** The response of `POST /feedback`. */
export type SubmitFeedbackResult = {
  /** ULID. Quoted in Slack and in the `mailto:` reply subject. */
  reportId: string
}

/**
 * Identity, resolved from the authenticated session — never from the payload.
 * The optional fields come from best-effort lookups that are allowed to fail
 * without failing the submission.
 */
export type FeedbackReporter = {
  userId: string
  orgId: string
  /** Verified email from the user record, distinct from `reporterEmail`. */
  accountEmail?: string
  displayName?: string
  /** Organisation name, for the Slack header. */
  accountName?: string
  /** Highest org-scoped role held in `orgId`. */
  role?: string
}

/**
 * The `Feedback Submitted` event detail: the client's submission plus
 * server-resolved identity. Consumed by the notification service.
 */
export type FeedbackReport = SubmitFeedbackInput & {
  reportId: string
  /** ISO 8601, server clock. */
  submittedAt: string
  reporter: FeedbackReporter
}

/**
 * Thrown by `UserServiceClient.submitFeedback` when the server returns 429,
 * so the widget can render a countdown instead of a generic failure.
 */
export class FeedbackRateLimitedError extends Error {
  readonly retryAfterSeconds: number

  constructor(retryAfterSeconds: number) {
    super(
      `Feedback rate limit reached — retry in ${retryAfterSeconds} second(s)`,
    )
    this.name = 'FeedbackRateLimitedError'
    this.retryAfterSeconds = retryAfterSeconds
  }
}
